import { useCallback, useEffect, useState, useRef } from "react";
import { Button, Card } from "flowbite-react";
import FormInput from "../forms/FormInput";
import toast from "react-hot-toast";

import {
  getInterests,
  getLanguages,
  searchInfluencers,
} from "../../api/instagram";
import {
  fetchLocations,
  mapLocations,
  fetchBrands,
  mapBrands,
  fetchTopics,
  mapTopics,
} from "../../services/fetch";
import {
  getFollowersOptions,
  getGenderOptions,
  getAgeOptions,
  getLastPostedOptions,
  getEngagementRateOptions,
  getContactInformationOptions,
  getFollowerGrowthRateIntervalOptions,
  getFollowerGrowthRateOptions,
  getAccountTypeOptions,
  getVerifiedOptions,
  getSponsoredPostsOptions,
  getWeights,
  getAudienceAgeOptions,
  getCredibilityOptions,
  getSortOptions,
  getReelsPlaysOptions
} from "../../services/modash";

import Lookalikes from "./Lookalikes";

const InstagramDiscovery = () => {
  const weights = getWeights();
  const weightTooltip = 'Start with the default % options. Then gradually change percentage to find the best influencers for you. The higher the percentage, the fewer results you\'ll get.';
  const [instagramInterests, setInstagramInterests] = useState([]);
  const [instagramLanguages, setInstagramLanguages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lookalikes, setLookalikes] = useState([]);
  const targetRef = useRef(null);

  const [sort, setSort] = useState({
    field: 'followers',
    direction: 'desc',
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [interests, languages] = await Promise.all([getInterests(), getLanguages()]);
        setInstagramInterests(interests);
        setInstagramLanguages(languages);
      } catch (error) {
        console.error('Error fetching data', error);
        toast.error('Error fetching data');
      }
    };
    fetchData();
  }, []);

  const [influencerFormData, setInfluencerFormData] = useState({
    location: null,
    followers: { min: null, max: null },
    reelsPlays: { min: null, max: null },
    gender: null,
    age: { min: null, max: null },
    interests: null,
    language: null,
    lastposted: null,
    engagementRate: null,
    hasContactDetails: ['email'],
    followerGrowthRate: { interval: null, operator: null, value: null },
    brands: null,
    accountTypes: null,
    isVerified: null,
    hasSponsoredPosts: null,
    bio: null,
    relevance: null,
    hashtags: null,
    mentions: null,
    keywords: null,
  });

  const [audienceFormData, setAudienceFormData] = useState({
    location: null,
    gender: null,
    age: null,
    credibility: null,
    interests: null,
    language: null,
  });

  const influencerFilters = [
    {
      type: 'multiSelectAsync',
      name: 'location',
      id: 'location',
      label: 'Influencer location',
      placeholder: 'Where are your influencers?',
      value: influencerFormData.location,
      width: 'w-80',
      fetchOptions: fetchLocations,
      mapResponse: mapLocations,
    },
    {
      type: 'selectRange',
      name: 'followers',
      id: 'followers',
      label: 'Followers',
      value: influencerFormData.followers,
      width: 'w-80',
      options: [
        getFollowersOptions(0, influencerFormData.followers.max || 0, 'from'),
        getFollowersOptions(influencerFormData.followers.min || 0, 0, 'to'),
      ],
    },
    {
      type: 'selectRange',
      name: 'reelsPlays',
      id: 'reelsPlays',
      label: 'Average Reels plays',
      value: influencerFormData.reelsPlays,
      width: 'w-80',
      options: [
        getReelsPlaysOptions(0, influencerFormData.reelsPlays.max || 0, 'from'),
        getReelsPlaysOptions(influencerFormData.reelsPlays.min || 0, 0, 'to'),
      ],
    },
    {
      type: 'select',
      name: 'gender',
      id: 'gender',
      label: 'Gender',
      value: influencerFormData.gender,
      width: 'w-48',
      options: getGenderOptions(),
      placeholder: 'Any',
    },
    {
      type: 'selectRange',
      name: 'age',
      id: 'age',
      label: 'Age',
      value: influencerFormData.age,
      width: 'w-80',
      options: [
        getAgeOptions(0, influencerFormData.age.max || 0),
        getAgeOptions(influencerFormData.age.min || 0, 0),
      ],
    },
    {
      type: 'multiSelect',
      name: 'interests',
      id: 'interests',
      label: 'Interests',
      placeholder: 'Any',
      value: influencerFormData.interests,
      width: 'w-96',
      options: instagramInterests,
    },
    {
      type: 'multiSelect',
      name: 'language',
      id: 'language',
      label: 'Language',
      placeholder: 'Any',
      value: influencerFormData.language,
      width: 'w-60',
      options: instagramLanguages,
    },
    {
      type: 'select',
      name: 'lastposted',
      id: 'lastposted',
      label: 'Last post',
      value: influencerFormData.lastposted,
      width: 'w-40',
      options: getLastPostedOptions(),
      placeholder: 'Any',
    },
    {
      type: 'select',
      name: 'engagementRate',
      id: 'engagementRate',
      label: 'Engagement rate',
      value: influencerFormData.engagementRate,
      width: 'w-48',
      options: getEngagementRateOptions(),
      placeholder: 'Any',
      tooltip: 'Find influencers based on how engaged the influencer\'s audience is. Engagement Rate is the ratio of likes divided by followers.',
    },
    {
      type: 'multiSelect',
      name: 'hasContactDetails',
      id: 'hasContactDetails',
      label: 'Contact information',
      value: influencerFormData.hasContactDetails,
      width: 'w-48',
      options: getContactInformationOptions(),
      defaultValue: [
        {
          label: 'Email',
          value: 'email',
        },
      ],
      placeholder: 'Any',
      tooltip: 'Find influencers based on theis available Contact Information. Contact Information is based on publicly available data. We constantly add new and also remove old contact Information that is no more publicly available.',
    },
    {
      type: 'intervalSelect',
      name: 'followerGrowthRate',
      id: 'followerGrowthRate',
      label: 'Growth rate',
      value: influencerFormData.followerGrowthRate,
      width: 'w-44',
      options: [
        getFollowerGrowthRateIntervalOptions(),
        getFollowerGrowthRateOptions(),
      ],
      placeholder: 'Any',
      tooltip: 'Find influencers based on the Growth Rate of the influencer\'s follower count over the chosen time frame.',
      defaultValue: { label: '>150%', value: { operator: 'gt', value: 150 } },
    },
    {
      type: 'multiSelectAsync',
      name: 'brands',
      id: 'brands',
      label: 'Partnerships',
      placeholder: 'Any',
      value: influencerFormData.brands,
      width: 'w-60',
      fetchOptions: fetchBrands,
      mapResponse: mapBrands,
      tooltip: 'Find influencers who have a sponsored post with at least one of the selected brands. We track sponsored posts by Instagram\'s Paid Partnership feature or by hashtags like #ad, #sponsored, #paid, and others.',
    },
    {
      type: 'multiSelect',
      name: 'accountTypes',
      id: 'accountTypes',
      label: 'Account type',
      value: influencerFormData.accountTypes,
      width: 'w-48',
      options: getAccountTypeOptions(),
      placeholder: 'Any',
      tooltip: 'Find influencers by their Instagram Account Type: Business, Creator, or Regular (non business).',
    },
    {
      type: 'select',
      name: 'isVerified',
      id: 'isVerified',
      label: 'Verified status',
      value: influencerFormData.isVerified,
      width: 'w-40',
      options: getVerifiedOptions(),
      placeholder: 'Any',
    },
    {
      type: 'select',
      name: 'hasSponsoredPosts',
      id: 'hasSponsoredPosts',
      label: 'Content Type',
      value: influencerFormData.hasSponsoredPosts,
      width: 'w-72',
      options: getSponsoredPostsOptions(),
      placeholder: 'Any',
    },
    {
      type: 'text',
      name: 'bio',
      id: 'bio',
      label: 'Bio',
      value: influencerFormData.bio,
      width: 'w-96',
      placeholder: 'Any',
      tooltip: 'Find influencers by words used in their bio/description. Imagine how influencers would describe themselves & try out different words or phrases to get best results.'
    },
    {
      type: 'multiSelectAsync',
      name: 'relevance',
      id: 'relevance',
      label: 'Topics',
      value: influencerFormData.relevance,
      width: 'w-64',
      placeholder: 'Topics',
      tooltip: 'Find influencers by the topics they post about. Topics are groups of related hashtags. Example: The hashtag #car is related to: #cars, #carlovers, #automobile and others. Use niche hashtags for a specific topics to find influencers that mention those hashtags very often.',
      fetchOptions: fetchTopics,
      mapResponse: mapTopics,
    },
    {
      type: 'text',
      name: 'hashtags',
      id: 'hashtags',
      label: 'Hashtags',
      value: influencerFormData.hashtags,
      width: 'w-64',
      placeholder: '#hashtag',
      tooltip: 'Find influencers who have at least once posted with a specific #hashtag in their content.'
    },
    {
      type: 'text',
      name: 'mentions',
      id: 'mentions',
      label: 'Mention',
      value: influencerFormData.mentions,
      width: 'w-64',
      placeholder: '@username',
      tooltip: 'Find influencers who have at least once tagged or mentioned a @username in their content'
    },
    {
      type: 'text',
      name: 'keywords',
      id: 'keywords',
      label: 'Keywords or Phrase',
      value: influencerFormData.keywords,
      width: 'w-96',
      placeholder: 'Keyword',
      tooltip: 'Find influencers by a phrase they used within the caption of their post.'
    },
  ];

  const audienceFilters = [
    {
      type: 'multiSelectWeightAsync',
      name: 'audienceLocation',
      id: 'location',
      label: 'Audience Location',
      placeholder: 'Where is your audience?',
      value: audienceFormData.location,
      width: 'w-80',
      fetchOptions: fetchLocations,
      mapResponse: mapLocations,
      tooltip: weightTooltip,
      weights,
      defaultWeight: 0.2,
    },
    {
      type: 'multiSelectWeight',
      name: 'audienceGender',
      id: 'gender',
      label: 'Gender',
      placeholder: 'Add Gender',
      value: audienceFormData.gender,
      width: 'w-44',
      tooltip: weightTooltip,
      weights,
      defaultWeight: 0.5,
      options: getGenderOptions(true),
    },
    {
      type: 'multiSelectWeight',
      name: 'audienceAge',
      id: 'age',
      label: 'Age',
      placeholder: 'Add Age',
      value: audienceFormData.age,
      width: 'w-44',
      tooltip: weightTooltip,
      weights,
      defaultWeight: 0.3,
      options: getAudienceAgeOptions(),
    },
    {
      type: 'select',
      name: 'credibility',
      id: 'credibility',
      label: 'Fake followers',
      placeholder: 'Any',
      value: audienceFormData.credibility,
      width: 'w-36',
      tooltip: 'Find influencers based on the percentage of fake followers they have. Everybody has fake followers and up to 25% is considered "normal".',
      options: getCredibilityOptions(),
    },
    {
      type: 'multiSelectWeight',
      name: 'audienceInterests',
      id: 'interests',
      label: 'Interests',
      placeholder: 'Any',
      value: influencerFormData.interests,
      width: 'w-96',
      options: instagramInterests,
      tooltip: weightTooltip,
      weights,
      defaultWeight: 0.3,
    },
    {
      type: 'multiSelectWeight',
      name: 'audienceLanguage',
      id: 'language',
      label: 'Language',
      placeholder: 'Any',
      value: influencerFormData.language,
      width: 'w-60',
      options: instagramLanguages,
      tooltip: weightTooltip,
      weights,
      defaultWeight: 0.2,
    },
  ];

  const handleNestedFormChange = (e, setFormData) => {
    const { name, value } = e.target;

    if (['followers', 'reelsPlays', 'age', 'followerGrowthRate'].includes(name)) return;

    setFormData(prevState => {
      const keys = name.split('.');
      const updatedFormData = { ...prevState };

      keys.reduce((acc, key, index) => {
        if (index === keys.length - 1) {
          acc[key] = value;
        } else {
          if (!acc[key]) acc[key] = {};
          return acc[key];
        }

        return null;
      }, updatedFormData);

      return updatedFormData;
    });
  }

  const handleInfluencerFilterChange = useCallback(
    e => handleNestedFormChange(e, setInfluencerFormData),
    []
  );

  const handleAudienceFilterChange = useCallback(
    e => handleNestedFormChange(e, setAudienceFormData),
    []
  );

  const resetPageAndFetchInfluencers = () => {
    setPage(1);
    fetchInfluencers();
  }

  const fetchInfluencers = useCallback(async (newPage = 0) => {
    setIsProcessing(true);

    try {
      const response = await searchInfluencers({
        data: {
          filter: {
            audience: audienceFormData,
            influencer: influencerFormData,
          },
          sort,
          page: newPage || page,
        },
        network: 'instagram',
      });

      setLookalikes(response.lookalikes);
      setTotalPages(Math.floor(response.total / 15));
      setTotal(response.total);

      if (targetRef.current) {
        targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error(error);
      toast.error(error);
    } finally {
      setIsProcessing(false);
    }
  }, [influencerFormData, audienceFormData, sort, page]);

  const handleSortChange = useCallback((e) => {
    if (typeof e.target.value === 'string') {
      setSort(prevState => ({
        ...prevState,
        field: e.target.value,
      }));
    }
  }, []);

  const handlePageChange = (value) => {
    setPage(value);
    fetchInfluencers(value);
  }

  return (
    <>
      <Card>
        <div className="flex p-4 rounded-md bg-gray-100 flex-col gap-4">
          <h2 className="text-gray-900 font-bold">Influencer filters</h2>
          <div className="flex flex-wrap gap-x-2 gap-y-4">
            {influencerFilters.map((input, key) => (
              <div
                className={input.width}
                key={key}
              >
                <FormInput
                  input={input}
                  onChange={handleInfluencerFilterChange}
                />
              </div>
            ))}
          </div>
          <hr />
          <h2 className="text-gray-900 font-bold">Audience filters</h2>
          <div className="flex flex-wrap gap-x-2 gap-y-4">
            {audienceFilters.map((input, key) => (
              <div
                className={input.width}
                key={key}
              >
                <FormInput
                  input={input}
                  onChange={handleAudienceFilterChange}
                />
              </div>
            ))}
          </div>
          <hr />
          <h2 className="text-gray-900 font-bold">Sort by</h2>
          <div className="flex flex-wrap gap-x-2 gap-y-4">
            <div className="w-64">
              <FormInput
                input={{
                  type: 'select',
                  name: 'sort',
                  id: 'sort',
                  label: 'Sort by',
                  placeholder: 'Followers',
                  options: getSortOptions(),
                }}
                onChange={handleSortChange}
              />
            </div>
          </div>
          <hr />
          <Button
            onClick={resetPageAndFetchInfluencers}
            isProcessing={isProcessing}
            disabled={isProcessing}
          >
            Show influencers
          </Button>
        </div>
      </Card>
      <div ref={targetRef} className="mt-4">
        <Lookalikes
          lookalikes={lookalikes}
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          total={total}
        />
      </div>
    </>
  );
}

export default InstagramDiscovery;
